import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal, SectionLabel } from "./Reveal";
import { IMAGES } from "@/lib/site";
import { Globe, Building2, ShieldCheck } from "lucide-react";

const PILLARS = [
  { icon: Globe, title: "Global reach", desc: "Serving enterprises across 150+ countries and every time zone." },
  { icon: Building2, title: "Enterprise-first", desc: "Engineered for the scale, security, and governance large organizations require." },
  { icon: ShieldCheck, title: "Reliable & secure", desc: "Resilient cloud infrastructure with enterprise-grade protection built in." },
];

export const GlobalPresence = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="global" data-testid="global-section" ref={ref} className="relative overflow-hidden bg-lark-ink">
      <motion.img
        style={{ y }}
        src={IMAGES.singapore}
        alt="Singapore skyline"
        className="absolute inset-0 h-[116%] w-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-lark-ink via-lark-ink/85 to-lark-ink/70" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 py-24 lg:py-32">
        <div className="max-w-2xl">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-lark-blue">
              <span className="h-px w-8 bg-lark-blue/60" />
              Global Presence
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-semibold tracking-tight text-white leading-[1.1]">
              A truly international, enterprise-focused technology company
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-white/70">
              From our Singapore headquarters, Lark supports organizations around the world
              with cloud-based collaboration built to scale across borders, teams, and
              industries.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid sm:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={0.12 + i * 0.08}>
                <div
                  className="h-full rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-7 hover:bg-white/[0.08] transition-colors duration-300"
                  data-testid={`global-pillar-${i}`}
                >
                  <span className="grid place-items-center h-12 w-12 rounded-xl bg-lark-blue text-white mb-5">
                    <Icon size={22} />
                  </span>
                  <h3 className="font-display text-xl font-semibold text-white">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/65">{p.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
